import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ProjectCreationSettingsService, ProjectCreationEligibility, ProjectCreationSettings } from '../../services/projectCreationSettingsService';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import '../../styles/components/ProjectCreationEligibilityCard.css';

interface ProjectCreationEligibilityCardProps {
  className?: string;
  initialEligibility?: ProjectCreationEligibility;
  initialSettings?: ProjectCreationSettings;
}

const ProjectCreationEligibilityCard: React.FC<ProjectCreationEligibilityCardProps> = ({
  className = '',
  initialEligibility,
  initialSettings
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [eligibility, setEligibility] = useState<ProjectCreationEligibility | null>(null);
  const [settings, setSettings] = useState<ProjectCreationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialEligibility && initialSettings) {
      // Use pre-fetched data if provided
      setEligibility(initialEligibility);
      setSettings(initialSettings);
      setLoading(false);
    } else if (user) {
      // Otherwise fetch data
      checkEligibility();
    }
  }, [user, initialEligibility, initialSettings]);

  const checkEligibility = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Get both eligibility and settings
      const [eligibilityResult, settingsResult] = await Promise.all([
        ProjectCreationSettingsService.checkEligibility(user.id),
        ProjectCreationSettingsService.getSettings()
      ]);

      setEligibility(eligibilityResult);
      setSettings(settingsResult);
    } catch (err) {
      console.error('Error checking project creation eligibility:', err);
      setError('Failed to check project creation eligibility');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkedInConnect = () => {
    // Redirect to LinkedIn OAuth
    window.location.href = `${process.env.REACT_APP_SUPABASE_URL}/auth/v1/authorize?provider=linkedin_oidc&redirect_to=${window.location.origin}/profile/me`;
  };

  if (loading) {
    return (
      <div className={`project-creation-eligibility-card ${className}`}>
        <div className="eligibility-header">
          <h3 className="section-title">
            <i className="bi bi-rocket-takeoff"></i>
            {t('projectEligibility.projectCreation')}
          </h3>
        </div>
        <div className="eligibility-loading">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">{t('projectEligibility.checkingEligibility')}</span>
          </div>
          <span className="loading-text">{t('projectEligibility.checkingEligibility')}</span>
        </div>
      </div>
    );
  }

  if (error || !eligibility || !settings) {
    return (
      <div className={`project-creation-eligibility-card ${className}`}>
        <div className="eligibility-header">
          <h3 className="section-title">
            <i className="bi bi-rocket-takeoff"></i>
            {t('projectEligibility.projectCreation')}
          </h3>
        </div>
        <div className="eligibility-error">
          <i className="bi bi-exclamation-triangle"></i>
          <span>{error || t('projectEligibility.unableToCheck')}</span>
          <button className="retry-btn" onClick={checkEligibility}>
            <i className="bi bi-arrow-clockwise"></i>
            {t('projectEligibility.retry')}
          </button>
        </div>
      </div>
    );
  }

  const { canCreate, reasons } = eligibility;
  const hasPointsRequirement = settings.minimum_points_required.enabled;
  const hasLinkedInRequirement = settings.linkedin_auth_required.enabled;

  // If no requirements are enabled, show simple eligible status
  if (!hasPointsRequirement && !hasLinkedInRequirement) {
    return (
      <div className={`project-creation-eligibility-card ${className}`}>
        <div className="eligibility-header">
          <h3 className="section-title">
            <i className="bi bi-rocket-takeoff"></i>
            {t('projectEligibility.projectCreation')}
          </h3>
        </div>
        <div className="eligibility-status eligible">
          <div className="status-icon">
            <i className="bi bi-check-circle-fill"></i>
          </div>
          <div className="status-content">
            <div className="status-title">{t('projectEligibility.readyToCreate')}</div>
            <div className="status-description">{t('projectEligibility.canCreateAnytime')}</div>
          </div>
        </div>
        <div className="eligibility-actions">
          <Link to="/create-pre-release-project" className="btn btn-primary">
            <i className="bi bi-plus-circle"></i>
            {t('projectEligibility.createProject')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`project-creation-eligibility-card ${className}`}>
      <div className="eligibility-header">
        <h3 className="section-title">
          <i className="bi bi-rocket-takeoff"></i>
          {t('projectEligibility.projectCreation')}
        </h3>
        <div className={`eligibility-badge ${canCreate ? 'eligible' : 'blocked'}`}>
          <i className={`bi ${canCreate ? 'bi-check-circle-fill' : 'bi-lock-fill'}`}></i>
          {canCreate ? t('projectEligibility.eligible') : t('projectEligibility.requirementsNotMet')}
        </div>
      </div>

      {/* Overall Status */}
      <div className={`eligibility-status ${canCreate ? 'eligible' : 'blocked'}`}>
        <div className="status-icon">
          <i className={`bi ${canCreate ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'}`}></i>
        </div>
        <div className="status-content">
          <div className="status-title">
            {canCreate ? t('projectEligibility.readyToCreateProjects') : t('projectEligibility.requirementsNotMet')}
          </div>
          <div className="status-description">
            {canCreate
              ? t('projectEligibility.meetsAllRequirements')
              : t('projectEligibility.completeRequirements')
            }
          </div>
        </div>
      </div>

      {/* Requirements List */}
      <div className="requirements-list">
        {/* Points Requirement */}
        {hasPointsRequirement && (
          <div className={`requirement-item ${reasons.insufficientPoints ? 'not-met' : 'met'}`}>
            <div className="requirement-icon">
              <i className={`bi ${reasons.insufficientPoints ? 'bi-x-circle' : 'bi-check-circle'}`}></i>
            </div>
            <div className="requirement-content">
              <div className="requirement-title">{t('projectEligibility.pointsRequirement')}</div>
              <div className="requirement-description">
                {reasons.insufficientPoints ? (
                  t('projectEligibility.needPoints', {
                    required: reasons.insufficientPoints.required,
                    current: reasons.insufficientPoints.current
                  })
                ) : (
                  t('projectEligibility.hasEnoughPoints', { value: settings.minimum_points_required.value })
                )}
              </div>
              {reasons.insufficientPoints && (
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${Math.min(100, (reasons.insufficientPoints.current / reasons.insufficientPoints.required) * 100)}%`
                    }}
                  />
                  <div className="progress-text">
                    {t('projectEligibility.pointsProgress', {
                      current: reasons.insufficientPoints.current,
                      required: reasons.insufficientPoints.required
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* LinkedIn Requirement */}
        {hasLinkedInRequirement && (
          <div className={`requirement-item ${reasons.linkedinRequired ? 'not-met' : 'met'}`}>
            <div className="requirement-icon">
              <i className={`bi ${reasons.linkedinRequired ? 'bi-x-circle' : 'bi-check-circle'}`}></i>
            </div>
            <div className="requirement-content">
              <div className="requirement-title">{t('projectEligibility.linkedinAuthentication')}</div>
              <div className="requirement-description">
                {reasons.linkedinRequired ? (
                  t('projectEligibility.connectLinkedinToVerify')
                ) : (
                  t('projectEligibility.linkedinConnected')
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="eligibility-actions">
        {canCreate ? (
          <Link to="/create-pre-release-project" className="btn btn-primary">
            <i className="bi bi-plus-circle"></i>
            {t('projectEligibility.createProject')}
          </Link>
        ) : (
          <div className="action-buttons">
            {reasons.insufficientPoints && (
              <Link to="/tasks" className="btn btn-outline-primary">
                <i className="bi bi-trophy"></i>
                {t('projectEligibility.earnPoints')}
              </Link>
            )}
            {reasons.linkedinRequired && (
              <button
                className="btn btn-outline-primary"
                onClick={handleLinkedInConnect}
              >
                <i className="bi bi-linkedin"></i>
                {t('projectEligibility.connectLinkedin')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectCreationEligibilityCard;